<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import Prompt from '../../Prompt.svelte';
    import Svg from '../Svg.svelte';
    import HotspotChoice from '../HotspotChoice.svelte';
    import ChoiceFeedbackBlock from '../../feedback/ChoiceFeedbackBlock.svelte';
    import { feedbackTypes } from '../../feedback/choiceFeedbackHelper.js';
    import { getInteractionStateStore } from '../../../itemsStateStore.js';
    import { getItemSessionStatusStore } from '../../../itemsSessionStatusStore.js';
    import itemSessionStatus from '../../../itemSessionStatus.js';
    import { hasClass } from '../../util/attributes.js';
    import { calculateScalingFactor, getScaledCoords, getUsableHeight } from '../util/scaling.js';
    import { getChoiceNumericLabel, isRTLElement } from '../util/focusorder.js';
    import extendAriaLabel from './util/ariaLabelGenerator.js';
    import forwardFocusToChoice from '../util/actions/forwardFocusToChoice.js';
    import { arrowKeysFocusLoop } from '@oat-sa-private/ui-core';
    import preventSpaceScroll from '../../util/actions/preventSpaceScroll.js';
    import resizeObserve from '../../util/actions/resizeObserve.js';
    import { DeferredPromise } from '../../util/promise.js';
    import { getContext, untrack } from 'svelte';
    import { SvelteSet } from 'svelte/reactivity';
    import { resolveImage } from '../util/resolveImage.js';

    const qtiClass = 'qti-hotspotInteraction';

    const baseType = 'identifier';

    let {
        // keys for state store:
        itemIdentifier,
        responseIdentifier,
        // inherited item-level QTI attributes:
        language,
        id,
        dir,
        // inherited aria attributes:
        role,
        ariaAttrs = {},
        dataAttrs = {},
        // interaction-level QTI attributes:
        disabled = false,
        prompt,
        maxChoices = 1,
        minChoices = 0,
        classes = '',
        cardinality,
        /**
         * @typedef HotspotChoice - mapped from QTI HotspotChoice
         * @property {String} key
         * @property {String} shape
         * @property {String} coords
         * @property {String} hotspotLabel - for SR
         * @property {Object} [svg] - not passed, but set by interaction
         */
        /**
         * @type {HotspotChoice[]} - from itemData choices
         */
        choices = [],
        /**
         * @typedef ImgObject - background image of the graphic interaction
         * @property {String} data - equivalent to src
         * @property {Number} width
         * @property {Number} height
         */
        /**
         * @type {ImgObject}
         */
        imgObject
    } = $props();

    // svelte-ignore state_referenced_locally
    const itemContext = getContext(itemIdentifier);
    const instructionsLang = itemContext && itemContext.getInstructionsLang();

    // svelte-ignore state_referenced_locally
    const interactionStateStore = getInteractionStateStore(itemIdentifier, responseIdentifier),
        itemSessionStatusStore = getItemSessionStatusStore(itemIdentifier);

    // svelte-ignore non_reactive_update
    let qtiMaxChoicesMessage, qtiMinChoicesMessage, unselectedHidden, imgSrc;

    setDerivedProps();

    function setDerivedProps() {
        qtiMaxChoicesMessage = dataAttrs['data-max-selections-message'];
        qtiMinChoicesMessage = dataAttrs['data-min-selections-message'];

        unselectedHidden = hasClass(classes, 'qti-unselected-hidden');

        //adjust constraints; invalid constraint means no constraint
        if (maxChoices > 1 && maxChoices >= choices.length) {
            maxChoices = 0;
        }
        if (minChoices > choices.length) {
            minChoices = 0;
            maxChoices = cardinality === 'single' ? 1 : 0;
        }
        if (minChoices > 0 && maxChoices > 0 && minChoices > maxChoices) {
            minChoices = 0;
            maxChoices = cardinality === 'single' ? 1 : 0;
        }

        if (!cardinality) {
            cardinality = maxChoices === 1 ? 'single' : 'multiple';
        }

        imgSrc = resolveImage(itemContext, imgObject.data);
    }

    /**
     * @type {String[]} a new list of the choiceKeys, ordered into tabbing order
     * Initially undefined, but set once only, after all the bounding boxes are known
     */
    let choiceKeysInTabOrder = $state();

    let hasFocus = $state(false);
    let lastFocusedChoiceKey = $state();

    let interactionElement = $state();
    let isRTL = $derived(interactionElement && isRTLElement(interactionElement));

    let windowHeight = $state();
    let containerWidth = $state();

    //image loading
    const imageLoadingPromise = new DeferredPromise();
    itemContext.registerLoadingElement(imageLoadingPromise.promise);

    /**
     * Calculate the SVG scaling factor based on document dimensions, and set scaled image dimensions.
     * Scaling factor which should be applied to all SVG children
     */
    let scalingFactor = $derived.by(() => {
        if (containerWidth && windowHeight) {
            const usableHeight = getUsableHeight(windowHeight);
            return calculateScalingFactor(imgObject.width, imgObject.height, containerWidth, usableHeight);
        } else {
            return 1;
        }
    });
    let imgWidth = $derived(scalingFactor * imgObject.width);
    let imgHeight = $derived(scalingFactor * imgObject.height);

    /**
     * selected choice list
     * @type {Set} - initially empty
     */
    let selected = new SvelteSet();

    let isInteractionFocused = $state(false); // becomes true when interaction is focused for the first time

    let disabledBySession = $derived($itemSessionStatusStore === itemSessionStatus.closed);

    // do initial response definition
    if (!interactionStateStore.hasResponse()) {
        interactionStateStore.merge({ qtiClass });
        storeResponse();
    }

    loadResponse();
    $effect(() => {
        if ($interactionStateStore) {
            untrack(() => {
                loadResponse();
            });
        }
    });

    /**
     * Load the interaction response
     * (wrap string response identifier into array of identifiers if cardinality single)
     */
    function loadResponse() {
        let storedResponse = interactionStateStore.getResponseValue();
        if (cardinality === 'single' && storedResponse && storedResponse.length) {
            storedResponse = [storedResponse];
        }
        if (typeof storedResponse === 'undefined' || storedResponse === null) {
            storedResponse = [];
        }
        selected.clear();
        for (const sr of storedResponse) {
            selected.add(sr);
        }
    }

    /**
     * Format and store selected value in interactionStateStore
     * (unpack array of identifiers to single string identifier if cardinality single)
     */
    function storeResponse() {
        let value = Array.from(selected);

        if (cardinality === 'single' && value.length) {
            value = value[0];
        }

        interactionStateStore.setResponseValue(
            {
                cardinality,
                baseType,
                value
            },
            // validity:
            getValidity()
        );
    }

    /**
     * Get the validity of the current state
     * @returns {Boolean}
     */
    function getValidity() {
        if (maxChoices > 0 && selected.size > maxChoices) {
            return false;
        }
        if (minChoices > 0 && selected.size < minChoices) {
            return false;
        }
        return true;
    }

    /**
     * Handler for background image load event
     */
    function handleBackgroundImageLoad() {
        imageLoadingPromise.resolve();
    }

    /**
     * Handler for background image load error
     * @param {Error} err
     */
    function handleBackgroundImageError(err) {
        imageLoadingPromise.reject(err);
    }

    /**
     * Toggles the presence of the actioned choice within the `selected` set
     * Enforces the maxChoices limit only if maxChoices is 1
     * @param {Object} event
     */
    function handleChoiceChange(event) {
        const choiceKey = event.key;

        hasFocus = focusChoiceWithKey(choiceKey);

        if (selected.has(choiceKey)) {
            selected.delete(choiceKey);
        } else {
            if (cardinality === 'single' && selected.size === 1) {
                // must not exceed 1 choice in single cardinality: replace set
                selected.clear();
            }
            selected.add(choiceKey);
        }
        storeResponse();

        dispatchTraceInteraction(event);
    }

    /**
     * Flag that focus jumps inside interaction
     */
    function handleInteractionFocusIn() {
        if (!isInteractionFocused) {
            isInteractionFocused = true;
        }
    }

    /**
     * Measure interaction container's width
     * @param {CustomEvent} event
     */
    function handleInteractionResized(event) {
        const { width } = event.detail;
        if (width) {
            containerWidth = width;
        }
    }

    /**
     * Focuses a choice and sets it as the last focused choice
     * Also arranges it to the front
     * @param {String} choiceKey
     * @returns {Boolean} true if focused
     */
    export function focusChoiceWithKey(choiceKey) {
        const thisChoice = choices.find(choice => choice.key === choiceKey);
        if (thisChoice && thisChoice.svg) {
            if (thisChoice.svg.parent) {
                thisChoice.svg.parent('.hotspot-choice').front();
            }
            thisChoice.svg.node.focus();
            return true;
        }
        return false;
    }

    /**
     * Dispatches a trace event
     * @param {Object} eventDetail
     */
    function dispatchTraceInteraction(eventDetail) {
        const eventDetailData = eventDetail.eventData;
        let position, pressedKey;
        if (eventDetailData.type === 'click') {
            position = {
                clientX: eventDetailData.clientX,
                clientY: eventDetailData.clientY,
                screenX: eventDetailData.screenX,
                screenY: eventDetailData.screenY
            };
        } else {
            pressedKey = eventDetailData.key;
        }
        const eventData = {
            detail: {
                target: eventDetailData.target,
                domEventType: eventDetailData.type,
                qtiChoiceIdentifier: eventDetail.key,
                newResponse: [interactionStateStore.getResponseValue()],
                ...(position && { position }),
                ...(pressedKey && { pressedKey })
            }
        };

        const interactionEvent = new CustomEvent('interactiontrace', eventData);
        interactionElement.dispatchEvent(interactionEvent);
    }
</script>

<style>
    .qti-flow-container {
        &:focus {
            outline: none;
        }
        &:focus-visible {
            @add-mixin simple-outline;
        }
    }
</style>

<svelte:window bind:innerHeight={windowHeight} />

<div
    class="qti-interaction qti-blockInteraction {qtiClass} {classes}"
    lang={language}
    bind:this={interactionElement}
    use:resizeObserve={handleInteractionResized}
    onfocusin={handleInteractionFocusIn}
    {id}
    {dir}
    {role}
    aria-disabled={disabled || disabledBySession}
    {...ariaAttrs}
    {...dataAttrs}>
    {#if prompt}
        <Prompt blockTree={prompt} />
    {/if}

    <ChoiceFeedbackBlock
        {maxChoices}
        {minChoices}
        type={feedbackTypes.selectChoices}
        {qtiMaxChoicesMessage}
        {qtiMinChoicesMessage}
        {isInteractionFocused}
        {interactionElement}
        selectedNumber={selected.size}
        lang={itemContext && itemContext.getInstructionsLang()} />

    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div class="qti-flow-container" tabindex={hasFocus ? '0' : '-1'}>
        <!-- The inner tabstop takes you inside, and the outer tabstop is for getting back out -->
        <div
            class="qti-block"
            tabindex={hasFocus ? '-1' : '0'}
            use:forwardFocusToChoice={{
                choices,
                choiceKeysInTabOrder,
                lastFocusedChoiceKey,
                focusChoiceWithKey,
                hasFocus,
                isRTL
            }}
            onsetHasFocus={e => (hasFocus = e.detail)}
            onsetChoiceKeysTabOrder={e => (choiceKeysInTabOrder ??= e.detail)}
            onsetLastFocusedChoiceKey={e => (lastFocusedChoiceKey = e.detail)}
            onfocus={handleInteractionFocusIn}>
            <Svg
                {itemIdentifier}
                {imgWidth}
                {imgHeight}
                {imgSrc}
                onbackgroundImageLoad={handleBackgroundImageLoad}
                onbackgroundImageError={handleBackgroundImageError}>
                {#snippet renderContent()}
                    <g
                        tabindex="-1"
                        use:arrowKeysFocusLoop={{
                            focusByKey: focusChoiceWithKey,
                            keysInTabOrder: choiceKeysInTabOrder,
                            lastFocusedKey: lastFocusedChoiceKey,
                            isRTL
                        }}
                        use:preventSpaceScroll
                        onsetLastFocusedKey={e => (lastFocusedChoiceKey = e.detail)}>
                        {#each choices as choice (choice.key)}
                            <HotspotChoice
                                key={choice.key}
                                shape={choice.shape}
                                coords={getScaledCoords(choice.coords, scalingFactor)}
                                instructions={`${getChoiceNumericLabel(choice, choiceKeysInTabOrder)}, ${extendAriaLabel(
                                    disabled,
                                    selected.has(choice.key)
                                )}`}
                                {instructionsLang}
                                invisible={unselectedHidden}
                                selected={selected.has(choice.key)}
                                disabled={disabled || disabledBySession}
                                onmount={event => (choice.svg ??= event.svgGroup)}
                                onchange={handleChoiceChange} />
                        {/each}
                    </g>
                {/snippet}
            </Svg>
        </div>
    </div>
</div>
